import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { Spinner } from '~/components/ui/spinner';

interface ButtonProps {
  children?: ReactNode; // Button text or content (omit for size="icon" with an icon child)
  size?: 'sm' | 'md' | 'icon'; // sm is the standard; md is for standalone full-width CTAs; icon needs ariaLabel
  variant?: 'primary' | 'outline' | 'ghost' | 'link' | 'danger'; // Button variant
  startIcon?: ReactNode; // Icon before the text
  endIcon?: ReactNode; // Icon after the text
  onClick?: (e: React.MouseEvent) => void; // Click handler
  disabled?: boolean; // Disabled state
  loading?: boolean; // Spinner replaces startIcon and the button is disabled
  href?: string; // Renders a link with the same classes; disabled -> aria-disabled, no navigation
  title?: string;
  className?: string; // Merged last so a caller's override wins
  type?: 'button' | 'submit' | 'reset'; // Button type
  name?: string; // form field name, submitted with value when this button submits
  value?: string;
  form?: string; // id of the <form> a submit button belongs to, for a button rendered outside it
  testId?: string; // data-testid for the E2E suite
  ariaLabel?: string; // accessible name for icon-only buttons
  ariaExpanded?: boolean;
  ariaControls?: string;
  ariaCurrent?: 'page'; // marks the active page in a pager
}

const SIZE_CLASSES = {
  sm: 'h-9 px-4 text-sm',
  md: 'h-11 px-5 text-sm',
  icon: 'size-9 text-sm',
};

const VARIANT_CLASSES = {
  primary: 'bg-brand-500 text-white shadow-xs hover:bg-brand-600 disabled:bg-brand-300',
  danger: 'bg-error-500 text-white shadow-xs hover:bg-error-600 disabled:bg-error-300',
  outline:
    'bg-white text-gray-700 inset-ring inset-ring-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-400 dark:inset-ring-gray-700 dark:hover:bg-white/[0.03] dark:hover:text-gray-300',
  ghost: 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/[0.05]',
  link: 'h-auto px-0 text-brand-500 hover:underline dark:text-brand-400',
};

export const Button: React.FC<ButtonProps> = ({
  children,
  size = 'sm',
  variant = 'primary',
  startIcon,
  endIcon,
  onClick,
  className,
  disabled = false,
  loading = false,
  href,
  title,
  type = 'button',
  name,
  value,
  form,
  testId,
  ariaLabel,
  ariaExpanded,
  ariaControls,
  ariaCurrent,
}) => {
  const inactive = disabled || loading;
  const classes = twMerge(
    'focus-ring inline-flex items-center justify-center gap-2 rounded-lg font-medium transition',
    SIZE_CLASSES[size],
    VARIANT_CLASSES[variant],
    inactive && 'cursor-not-allowed opacity-50',
    className,
  );
  const content = (
    <>
      {loading ? (
        <Spinner size="sm" />
      ) : (
        startIcon && <span className="flex items-center">{startIcon}</span>
      )}
      {children}
      {endIcon && <span className="flex items-center">{endIcon}</span>}
    </>
  );

  if (href) {
    return (
      <Link
        to={href}
        data-testid={testId}
        aria-label={ariaLabel}
        aria-disabled={inactive || undefined}
        tabIndex={inactive ? -1 : undefined}
        title={title}
        className={classes}
        onClick={(e) => {
          if (inactive) e.preventDefault();
          else onClick?.(e);
        }}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      name={name}
      value={value}
      form={form}
      data-testid={testId}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      aria-current={ariaCurrent}
      aria-busy={loading || undefined}
      title={title}
      className={classes}
      onClick={onClick}
      disabled={inactive}
    >
      {content}
    </button>
  );
};
